import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { NETWORK_EXPERT_CATEGORIES, type NetworkExpert, type NetworkResource } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthProvider';
import { deleteStaffResourceFile, uploadStaffResourceFile } from '@/lib/bunnyStorage';
import { useToast } from '@/features/ui/toast/useToast';

const expertCategories = NETWORK_EXPERT_CATEGORIES.filter((c) => c.key !== 'all');
const CUSTOM_CATEGORY_KEY = '__custom__';

const emptyExpertForm = {
  full_name: '',
  role_title: '',
  handle: '',
  bio: '',
  category: 'consultant',
  specialties: '',
  email: '',
  whatsapp: '',
  avatar_url: '',
  is_online: false,
};

export function AdminNetworkPage() {
  const { profile, isStaff } = useAuth();
  const toast = useToast();
  const [experts, setExperts] = useState<NetworkExpert[]>([]);
  const [resources, setResources] = useState<NetworkResource[]>([]);

  const [exForm, setExForm] = useState(emptyExpertForm);
  const [editingExpertId, setEditingExpertId] = useState<string | null>(null);
  const [exAvatar, setExAvatar] = useState<File | null>(null);
  const exAvatarRef = useRef<HTMLInputElement | null>(null);

  const [editingResourceId, setEditingResourceId] = useState<string | null>(null);
  const [resKind, setResKind] = useState<'guide' | 'masterclass'>('guide');
  const [resTitle, setResTitle] = useState('');
  const [resDesc, setResDesc] = useState('');
  const [resUrl, setResUrl] = useState('');
  const [resVideo, setResVideo] = useState('');
  const [resDuration, setResDuration] = useState('');
  const [resFile, setResFile] = useState<File | null>(null);
  const resFileRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const selectedCategoryValue = expertCategories.some((item) => item.key === exForm.category)
    ? exForm.category
    : CUSTOM_CATEGORY_KEY;

  async function load() {
    const [e, r] = await Promise.all([
      supabase.from('network_experts').select('*').order('sort_order').order('full_name'),
      supabase.from('network_resources').select('*').order('kind').order('sort_order'),
    ]);
    if (e.error) toast.error(e.error.message);
    if (r.error) toast.error(r.error.message);
    setExperts((e.data ?? []) as NetworkExpert[]);
    setResources((r.data ?? []) as NetworkResource[]);
  }

  useEffect(() => {
    void load();
  }, []);

  function resetExpertForm() {
    setEditingExpertId(null);
    setExForm(emptyExpertForm);
    setExAvatar(null);
    if (exAvatarRef.current) exAvatarRef.current.value = '';
  }

  function startEditExpert(ex: NetworkExpert) {
    const knownCategory = expertCategories.some((item) => item.key === ex.category);
    setEditingExpertId(ex.id);
    setExForm({
      full_name: ex.full_name,
      role_title: ex.role_title ?? '',
      handle: ex.handle ?? '',
      bio: ex.bio ?? '',
      category: knownCategory ? ex.category || 'consultant' : ex.category || '',
      specialties: (ex.specialties ?? []).join(', '),
      email: ex.email ?? '',
      whatsapp: ex.whatsapp ?? '',
      avatar_url: ex.avatar_url ?? '',
      is_online: ex.is_online,
    });
    setExAvatar(null);
    if (exAvatarRef.current) exAvatarRef.current.value = '';
  }

  async function saveExpert(e: FormEvent) {
    e.preventDefault();
    if (!isStaff || !profile) return;
    setBusy(true);
    try {
      let avatar_storage_path: string | null | undefined;
      if (exAvatar) {
        const safe = exAvatar.name.replace(/[^\w.\-]+/g, '_');
        avatar_storage_path = `network/avatars/${Date.now()}_${safe}`;
        await uploadStaffResourceFile({ storagePath: avatar_storage_path, file: exAvatar });
      }
      const specialties = exForm.specialties
        .split(/[,;]/)
        .map((s) => s.trim())
        .filter(Boolean);
      const payload: Record<string, unknown> = {
        full_name: exForm.full_name.trim(),
        role_title: exForm.role_title.trim(),
        handle: exForm.handle.trim().replace(/^@/, ''),
        bio: exForm.bio.trim(),
        category: exForm.category === CUSTOM_CATEGORY_KEY ? '' : exForm.category.trim(),
        specialties,
        email: exForm.email.trim(),
        whatsapp: exForm.whatsapp.trim(),
        avatar_url: exForm.avatar_url.trim() || null,
        is_online: exForm.is_online,
        updated_at: new Date().toISOString(),
      };
      if (avatar_storage_path) payload.avatar_storage_path = avatar_storage_path;

      if (editingExpertId) {
        const { error: err } = await supabase.from('network_experts').update(payload).eq('id', editingExpertId);
        if (err) throw err;
        toast.success('Expert mis à jour.', 'Réseau');
      } else {
        const { error: err } = await supabase.from('network_experts').insert({
          ...payload,
          is_published: true,
          created_by: profile.id,
        });
        if (err) throw err;
        toast.success('Expert publié dans l’annuaire.', 'Réseau');
      }
      resetExpertForm();
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Enregistrement expert impossible');
    } finally {
      setBusy(false);
    }
  }

  async function toggleExpert(ex: NetworkExpert) {
    if (!isStaff) return;
    await supabase
      .from('network_experts')
      .update({ is_published: !ex.is_published, updated_at: new Date().toISOString() })
      .eq('id', ex.id);
    await load();
  }

  async function removeExpert(ex: NetworkExpert) {
    if (!isStaff || !window.confirm(`Supprimer ${ex.full_name} ?`)) return;
    await supabase.from('network_experts').delete().eq('id', ex.id);
    if (ex.avatar_storage_path) {
      try {
        await deleteStaffResourceFile({ storagePath: ex.avatar_storage_path });
      } catch {
        /* ignore */
      }
    }
    if (editingExpertId === ex.id) resetExpertForm();
    await load();
  }

  function resetResourceForm() {
    setEditingResourceId(null);
    setResKind('guide');
    setResTitle('');
    setResDesc('');
    setResUrl('');
    setResVideo('');
    setResDuration('');
    setResFile(null);
    if (resFileRef.current) resFileRef.current.value = '';
  }

  function startEditResource(r: NetworkResource) {
    setEditingResourceId(r.id);
    setResKind(r.kind);
    setResTitle(r.title);
    setResDesc(r.description ?? '');
    setResUrl(r.external_url ?? '');
    setResVideo(r.video_url ?? '');
    setResDuration(r.duration_label ?? '');
    setResFile(null);
    if (resFileRef.current) resFileRef.current.value = '';
  }

  async function saveResource(e: FormEvent) {
    e.preventDefault();
    if (!isStaff || !profile) return;
    setBusy(true);
    try {
      let storage_path: string | null | undefined;
      let file_name: string | null | undefined;
      if (resFile) {
        const safe = resFile.name.replace(/[^\w.\-]+/g, '_');
        storage_path = `network/${resKind}/${Date.now()}_${safe}`;
        await uploadStaffResourceFile({ storagePath: storage_path, file: resFile });
        file_name = resFile.name;
      }
      const external_url = resUrl.trim() || null;
      const video_url = resKind === 'masterclass' ? resVideo.trim() || null : null;

      const existing = editingResourceId ? resources.find((x) => x.id === editingResourceId) : null;
      const effectiveStorage = storage_path ?? existing?.storage_path ?? null;
      const effectiveExternal = external_url ?? (storage_path ? null : existing?.external_url ?? null);
      const effectiveVideo =
        resKind === 'masterclass'
          ? video_url ?? (storage_path || external_url ? null : existing?.video_url ?? null)
          : null;

      if (resKind === 'guide' && !effectiveStorage && !effectiveExternal) {
        toast.error('Ajoutez un fichier (PDF/Excel/PPT) ou une URL.');
        return;
      }
      if (resKind === 'masterclass' && !effectiveVideo && !effectiveStorage && !effectiveExternal) {
        toast.error('Ajoutez une URL vidéo, un fichier ou une URL externe.');
        return;
      }

      const payload: Record<string, unknown> = {
        kind: resKind,
        title: resTitle.trim(),
        description: resDesc.trim(),
        external_url: external_url,
        video_url: resKind === 'masterclass' ? video_url : null,
        duration_label: resDuration.trim(),
        updated_at: new Date().toISOString(),
      };
      if (storage_path) {
        payload.storage_path = storage_path;
        payload.file_name = file_name;
      }

      if (editingResourceId) {
        const { error: err } = await supabase.from('network_resources').update(payload).eq('id', editingResourceId);
        if (err) throw err;
        toast.success('Ressource mise à jour.', 'Réseau');
      } else {
        const { error: err } = await supabase.from('network_resources').insert({
          ...payload,
          file_name: file_name ?? null,
          storage_path: storage_path ?? null,
          is_published: true,
          created_by: profile.id,
        });
        if (err) throw err;
        toast.success(resKind === 'guide' ? 'Guide publié.' : 'Masterclass publiée.', 'Réseau');
      }
      resetResourceForm();
      await load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Publication impossible');
    } finally {
      setBusy(false);
    }
  }

  async function toggleResource(r: NetworkResource) {
    if (!isStaff) return;
    await supabase
      .from('network_resources')
      .update({ is_published: !r.is_published, updated_at: new Date().toISOString() })
      .eq('id', r.id);
    await load();
  }

  async function removeResource(r: NetworkResource) {
    if (!isStaff || !window.confirm(`Supprimer « ${r.title} » ?`)) return;
    await supabase.from('network_resources').delete().eq('id', r.id);
    if (r.storage_path) {
      try {
        await deleteStaffResourceFile({ storagePath: r.storage_path });
      } catch {
        /* ignore */
      }
    }
    if (editingResourceId === r.id) resetResourceForm();
    await load();
  }

  if (!isStaff) {
    return <p className="muted">Accès réservé au staff.</p>;
  }

  return (
    <div>
      <p className="muted" style={{ marginTop: 0 }}>
        Communautés (image + liens) : <Link to="/admin/communautes">Admin → Communautés</Link>
      </p>

      <section className="panel" style={{ marginBottom: '1rem' }}>
        <h2 style={{ marginTop: 0 }}>Annuaire des experts</h2>
        <form onSubmit={(e) => void saveExpert(e)} style={{ marginBottom: '1rem' }}>
          <div className="grid-2">
            <div className="field">
              <label>Nom complet</label>
              <input
                required
                value={exForm.full_name}
                onChange={(e) => setExForm({ ...exForm, full_name: e.target.value })}
              />
            </div>
            <div className="field">
              <label>Titre / rôle</label>
              <input value={exForm.role_title} onChange={(e) => setExForm({ ...exForm, role_title: e.target.value })} />
            </div>
            <div className="field">
              <label>Identifiant (@)</label>
              <input
                value={exForm.handle}
                onChange={(e) => setExForm({ ...exForm, handle: e.target.value })}
                placeholder="smartin"
              />
            </div>
            <div className="field">
              <label>Catégorie</label>
              <select
                value={selectedCategoryValue}
                onChange={(e) =>
                  setExForm({
                    ...exForm,
                    category: e.target.value === CUSTOM_CATEGORY_KEY ? CUSTOM_CATEGORY_KEY : e.target.value,
                  })
                }
              >
                {expertCategories.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label}
                  </option>
                ))}
                <option value={CUSTOM_CATEGORY_KEY}>Autre catégorie</option>
              </select>
            </div>
            <div className="field">
              <label>Email (réception messages)</label>
              <input type="email" value={exForm.email} onChange={(e) => setExForm({ ...exForm, email: e.target.value })} />
            </div>
            <div className="field">
              <label>WhatsApp</label>
              <input
                value={exForm.whatsapp}
                onChange={(e) => setExForm({ ...exForm, whatsapp: e.target.value })}
                placeholder="+225..."
              />
            </div>
          </div>
          {(exForm.category === CUSTOM_CATEGORY_KEY || !expertCategories.some((item) => item.key === exForm.category)) ? (
            <div className="field">
              <label>Catégorie personnalisée</label>
              <input
                value={exForm.category === CUSTOM_CATEGORY_KEY ? '' : exForm.category}
                onChange={(e) => setExForm({ ...exForm, category: e.target.value })}
                placeholder="Ex. Expert-comptable, Juriste, Coach..."
              />
            </div>
          ) : null}
          <div className="field">
            <label>Spécialités (séparées par virgule)</label>
            <input
              value={exForm.specialties}
              onChange={(e) => setExForm({ ...exForm, specialties: e.target.value })}
              placeholder="Agile, Scrum, UI/UX"
            />
          </div>
          <div className="field">
            <label>Bio</label>
            <textarea rows={3} value={exForm.bio} onChange={(e) => setExForm({ ...exForm, bio: e.target.value })} />
          </div>
          <div className="grid-2">
            <div className="field">
              <label>Photo (fichier)</label>
              <input
                ref={exAvatarRef}
                type="file"
                accept="image/*"
                onChange={(e) => setExAvatar(e.target.files?.[0] ?? null)}
              />
            </div>
            <div className="field">
              <label>Ou URL photo</label>
              <input value={exForm.avatar_url} onChange={(e) => setExForm({ ...exForm, avatar_url: e.target.value })} />
            </div>
          </div>
          <label style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
            <input
              type="checkbox"
              checked={exForm.is_online}
              onChange={(e) => setExForm({ ...exForm, is_online: e.target.checked })}
            />
            Afficher comme en ligne
          </label>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {editingExpertId ? 'Enregistrer les modifications' : 'Publier l’expert'}
            </button>
            {editingExpertId ? (
              <button type="button" className="btn btn-ghost" onClick={resetExpertForm}>
                Annuler
              </button>
            ) : null}
          </div>
        </form>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {experts.map((ex) => (
            <li
              key={ex.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '0.75rem',
                flexWrap: 'wrap',
                padding: '0.55rem 0',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <div>
                <strong>{ex.full_name}</strong>
                <div className="muted">
                  {ex.role_title} · {ex.category} · {ex.is_published ? 'publié' : 'masqué'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-ghost" onClick={() => startEditExpert(ex)}>
                  Modifier
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => void toggleExpert(ex)}>
                  {ex.is_published ? 'Masquer' : 'Publier'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => void removeExpert(ex)}>
                  Supprimer
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="panel">
        <h2 style={{ marginTop: 0 }}>Centre de ressources</h2>
        <form onSubmit={(e) => void saveResource(e)} style={{ marginBottom: '1rem' }}>
          <div className="grid-2">
            <div className="field">
              <label>Type</label>
              <select value={resKind} onChange={(e) => setResKind(e.target.value as 'guide' | 'masterclass')}>
                <option value="guide">Guide pratique</option>
                <option value="masterclass">Masterclass vidéo</option>
              </select>
            </div>
            <div className="field">
              <label>Titre</label>
              <input required value={resTitle} onChange={(e) => setResTitle(e.target.value)} />
            </div>
          </div>
          <div className="field">
            <label>Description</label>
            <textarea rows={2} value={resDesc} onChange={(e) => setResDesc(e.target.value)} />
          </div>
          {resKind === 'guide' ? (
            <div className="grid-2">
              <div className="field">
                <label>Fichier (PDF, Excel, PPT){editingResourceId ? ' — laisser vide pour conserver' : ''}</label>
                <input
                  ref={resFileRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                  onChange={(e) => setResFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <div className="field">
                <label>Ou URL</label>
                <input type="url" value={resUrl} onChange={(e) => setResUrl(e.target.value)} />
              </div>
            </div>
          ) : (
            <>
              <div className="field">
                <label>URL vidéo (YouTube, Vimeo…)</label>
                <input type="url" value={resVideo} onChange={(e) => setResVideo(e.target.value)} />
              </div>
              <div className="grid-2">
                <div className="field">
                  <label>Fichier vidéo (optionnel)</label>
                  <input
                    ref={resFileRef}
                    type="file"
                    accept="video/*,.mp4,.mov,.webm"
                    onChange={(e) => setResFile(e.target.files?.[0] ?? null)}
                  />
                </div>
                <div className="field">
                  <label>Durée affichée</label>
                  <input
                    value={resDuration}
                    onChange={(e) => setResDuration(e.target.value)}
                    placeholder="45 min"
                  />
                </div>
              </div>
            </>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button className="btn btn-primary" type="submit" disabled={busy}>
              {editingResourceId ? 'Enregistrer la ressource' : 'Publier la ressource'}
            </button>
            {editingResourceId ? (
              <button type="button" className="btn btn-ghost" onClick={resetResourceForm}>
                Annuler
              </button>
            ) : null}
          </div>
        </form>
        <ul style={{ listStyle: 'none', padding: 0 }}>
          {resources.map((r) => (
            <li
              key={r.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '0.75rem',
                flexWrap: 'wrap',
                padding: '0.55rem 0',
                borderBottom: '1px solid var(--line)',
              }}
            >
              <div>
                <strong>{r.title}</strong>
                <div className="muted">
                  {r.kind} · {r.is_published ? 'publié' : 'masqué'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-ghost" onClick={() => startEditResource(r)}>
                  Modifier
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => void toggleResource(r)}>
                  {r.is_published ? 'Masquer' : 'Publier'}
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => void removeResource(r)}>
                  Supprimer
                </button>
              </div>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}
